import React, { useState } from 'react';
import './Support.css';

const Support = () => {
  const [activeTab, setActiveTab] = useState('tickets');
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState([
    { sender: 'support', text: 'Hello! How can I help you today?', time: '2:30 PM' }
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);

  const supportTabs = [
    { id: 'tickets', label: 'Support Tickets', icon: '🎫' },
    { id: 'knowledge', label: 'Knowledge Base', icon: '📚' },
    { id: 'live-chat', label: 'Live Chat', icon: '💬' },
    { id: 'status', label: 'System Status', icon: '📊' }
  ];

  const knowledgeBase = [
    {
      category: 'Getting Started',
      articles: [
        { title: 'How to create your first project', views: 1250 },
        { title: 'Setting up your development environment', views: 980 },
        { title: 'Understanding our project workflow', views: 756 }
      ]
    },
    {
      category: 'Technical Support',
      articles: [
        { title: 'Troubleshooting common deployment issues', views: 2100 },
        { title: 'Database connection problems', views: 1450 },
        { title: 'API integration guide', views: 1890 }
      ]
    },
    {
      category: 'Account & Billing',
      articles: [
        { title: 'How to update your billing information', views: 650 },
        { title: 'Understanding your invoice', views: 420 },
        { title: 'Cancellation and refund policy', views: 380 }
      ]
    }
  ];

  const systemStatus = [
    { service: 'Web Services', status: 'operational', uptime: '99.9%' },
    { service: 'API Gateway', status: 'operational', uptime: '99.8%' },
    { service: 'Database', status: 'operational', uptime: '99.95%' },
    { service: 'CDN', status: 'operational', uptime: '99.7%' },
    { service: 'Email Services', status: 'operational', uptime: '99.9%' }
  ];

  const supportResponses = [
    "Can you explain your issue in more detail?",
    "Thanks for your message! We'll get back to you shortly.",
    "Our support team is reviewing your query.",
    "Please check our knowledge base for guidance.",
    "Got it! We will assist you soon."
  ];

  // Send Live Chat message
  const handleSendMessage = () => {
    if (!chatInput.trim()) return;

    const now = new Date();
    const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    setMessages(prev => [...prev, { sender: 'user', text: chatInput, time }]);
    setChatInput('');

    setTimeout(() => {
      const randomReply =
        supportResponses[Math.floor(Math.random() * supportResponses.length)];
      const replyTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setMessages(prev => [...prev, { sender: 'support', text: randomReply, time: replyTime }]);
    }, 1000);
  };

  // Knowledge Base search
  const handleSearch = () => {
    const query = searchQuery.toLowerCase();
    const filtered = knowledgeBase.map(category => {
      const articles = category.articles.filter(article =>
        article.title.toLowerCase().includes(query)
      );
      return { ...category, articles };
    }).filter(category => category.articles.length > 0);

    setFilteredArticles(filtered);
  };

  const displayCategories = filteredArticles.length > 0 ? filteredArticles : knowledgeBase;

  return (
    <div className="support">
      <div className="support-hero">
        <div className="container">
          <h1 className="support-title">Support Center</h1>
          <p className="support-subtitle">
            Get help when you need it. Our support team is here to assist you 24/7.
          </p>
        </div>
      </div>

      {/* TABS */}
      <section className="support-tabs section">
        <div className="container">
          <div className="tabs-container">
            {supportTabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="support-content section">
        <div className="container">

          {/* ===== SUPPORT TICKETS ===== */}
          {activeTab === 'tickets' && (
            <div className="tickets-content tab-active">
              <h2>Support Tickets</h2>
              <p>Submit a support ticket and our team will get back to you within 24 hours.</p>

              <div className="ticket-form">
                <form>
                  <div className="form-group">
                    <label htmlFor="ticket-subject">Subject</label>
                    <input type="text" id="ticket-subject" placeholder="Brief description of your issue" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="ticket-priority">Priority</label>
                    <select id="ticket-priority">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="ticket-category">Category</label>
                    <select id="ticket-category">
                      <option value="technical">Technical Issue</option>
                      <option value="billing">Billing Question</option>
                      <option value="feature">Feature Request</option>
                      <option value="bug">Bug Report</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="ticket-description">Description</label>
                    <textarea
                      id="ticket-description"
                      rows="6"
                      placeholder="Please provide detailed information about your issue..."
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label htmlFor="ticket-attachments">Attachments</label>
                    <input type="file" id="ticket-attachments" multiple />
                    <small>You can attach screenshots, logs, or other relevant files</small>
                  </div>

                  <button type="submit" className="btn">Submit Ticket</button>
                </form>
              </div>
            </div>
          )}

          {/* ===== KNOWLEDGE BASE ===== */}
          {activeTab === 'knowledge' && (
            <div className="knowledge-content tab-active">
              <h2>Knowledge Base</h2>
              <p>Find answers to common questions and learn how to use our services effectively.</p>

              <div className="search-box">
                <input
                  type="text"
                  placeholder="Ask a question..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="search-btn" onClick={handleSearch}>Ask</button>
              </div>

              <div className="knowledge-categories">
                {displayCategories.map((category, index) => (
                  <div key={index} className="knowledge-category">
                    <h3>{category.category}</h3>
                    <div className="articles-list">
                      {category.articles.map((article, idx) => (
                        <div key={idx} className="article-item">
                          <h4>{article.title}</h4>
                          <span className="article-views">{article.views} views</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== LIVE CHAT ===== */}
          {activeTab === 'live-chat' && (
            <div className="live-chat-content tab-active">
              <h2>Live Chat Support</h2>
              <div className="chat-container">
                <div className="chat-messages">
                  {messages.map((msg, idx) => (
                    <div key={idx} className={`message ${msg.sender}`}>
                      <div className="message-avatar">{msg.sender === 'user' ? '🧑' : '👨‍💻'}</div>
                      <div className="message-content">
                        <div className="message-text">{msg.text}</div>
                        <div className="message-time">{msg.time}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="chat-input">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
                  />
                  <button className="send-btn" onClick={handleSendMessage}>Send</button>
                </div>
              </div>
            </div>
          )}

          {/* ===== SYSTEM STATUS ===== */}
          {activeTab === 'status' && (
            <div className="status-content tab-active">
              <h2>System Status</h2>
              <div className="services-status">
                {systemStatus.map((service, index) => (
                  <div key={index} className="status-item">
                    <div className="service-name">{service.service}</div>
                    <div className={`service-status ${service.status}`}>{service.status}</div>
                    <div className="service-uptime">{service.uptime}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
};
export default Support;